import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { logger } from '@/utils/logger'

interface SlotFamilyRecord {
  id: string
  date: string
  day: string
  class_name: string
  volunteer_name: string | null
  volunteer_email: string | null
  volunteer_phone: string | null
  sibling_classes?: string[]
  has_siblings?: boolean
  school_year?: string
}

const ALL_CLASSES = [
  'כיתה א-1', 'כיתה א-2', 'כיתה א-3',
  'כיתה ב-1', 'כיתה ב-2', 'כיתה ב-3',
  'כיתה ג-1', 'כיתה ג-2', 'כיתה ג-3',
  'כיתה ד-1', 'כיתה ד-2', 'כיתה ד-3',
  'כיתה ה-1', 'כיתה ה-2', 'כיתה ה-3',
  'כיתה ו-1', 'כיתה ו-2', 'כיתה ו-3',
]

export function FamilyDutyReport() {
  const [slots, setSlots] = useState<SlotFamilyRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('2026-2027')
  const [calculatorTargetClass, setCalculatorTargetClass] = useState('כיתה ד-2')
  const [calculatorBaseStudents, setCalculatorBaseStudents] = useState(30)
  const [copiedSql, setCopiedSql] = useState(false)
  const [filterType, setFilterType] = useState<'all' | 'siblings_only'>('all')

  const fetchFamilyData = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('volunteer_slots')
        .select('*')
        .not('volunteer_email', 'is', null)
        .order('date', { ascending: true })

      if (error) throw error

      setSlots(data || [])
    } catch (err) {
      logger.error('Failed to fetch family duty records', {
        component: 'FamilyDutyReport',
        data: err
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchFamilyData()
  }, [])

  // Filter slots for the selected school year
  const activeYearSlots = useMemo(() => {
    return slots.filter(s => {
      if (s.school_year) return s.school_year === selectedSchoolYear
      // Fallback based on date range
      if (selectedSchoolYear === '2026-2027') return s.date >= '2026-09-01' && s.date <= '2027-08-31'
      return s.date >= '2025-09-01' && s.date <= '2026-08-31'
    })
  }, [slots, selectedSchoolYear])

  // KPIs
  const totalFamilies = activeYearSlots.length
  const multiChildFamilies = useMemo(() => {
    return activeYearSlots.filter(s => s.has_siblings || (s.sibling_classes && s.sibling_classes.length > 0))
  }, [activeYearSlots])

  const totalExemptionsCount = useMemo(() => {
    return multiChildFamilies.reduce((sum, s) => sum + (s.sibling_classes?.length || 0), 0)
  }, [multiChildFamilies])

  // Calculator logic for target class
  const targetClassExemptions = useMemo(() => {
    return activeYearSlots.filter(s => 
      s.sibling_classes && s.sibling_classes.includes(calculatorTargetClass)
    )
  }, [activeYearSlots, calculatorTargetClass])

  const calculatedRequiredDays = Math.max(0, calculatorBaseStudents - targetClassExemptions.length)

  // Filtered table data
  const filteredRecords = useMemo(() => {
    return activeYearSlots.filter(s => {
      if (filterType === 'siblings_only' && !s.has_siblings && (!s.sibling_classes || s.sibling_classes.length === 0)) {
        return false
      }

      if (!searchQuery.trim()) return true

      const query = searchQuery.toLowerCase()
      const name = (s.volunteer_name || '').toLowerCase()
      const email = (s.volunteer_email || '').toLowerCase()
      const phone = (s.volunteer_phone || '').toLowerCase()
      const className = (s.class_name || '').toLowerCase()
      const siblings = (s.sibling_classes || []).join(' ').toLowerCase()

      return name.includes(query) || email.includes(query) || phone.includes(query) || className.includes(query) || siblings.includes(query)
    })
  }, [activeYearSlots, filterType, searchQuery])

  // Copy SQL query for opening the calculated days
  const handleCopySql = () => {
    const sql = `WITH next_working_days AS (
  SELECT vs.date
  FROM volunteer_slots vs
  LEFT JOIN holidays h ON vs.date = h.date
  WHERE vs.school_year = '${selectedSchoolYear}'
    AND vs.date >= '${selectedSchoolYear === '2026-2027' ? '2026-09-01' : '2025-09-01'}'
    AND (vs.class_name = 'פתוח' OR vs.class_name IS NULL OR vs.class_name = '')
    AND EXTRACT(DOW FROM vs.date) != 6 -- Exclude Saturdays
    AND (h.type IS NULL OR h.type = 'camp' OR h.type = 'special') -- Exclude holidays, keep camps
  ORDER BY vs.date ASC
  LIMIT ${calculatedRequiredDays}
)
UPDATE volunteer_slots vs
SET 
  class_name = '${calculatorTargetClass}',
  updated_at = NOW()
FROM next_working_days w
WHERE vs.date = w.date
RETURNING vs.date, vs.day, vs.class_name;`

    navigator.clipboard.writeText(sql)
    setCopiedSql(true)
    setTimeout(() => setCopiedSql(false), 3000)
  }

  // Export CSV
  const handleExportCsv = () => {
    const headers = ['שם מתנדב', 'אימייל', 'טלפון', 'כיתת תורנות', 'תאריך תורנות', 'יום', 'כיתות אחים פטורות']
    const rows = filteredRecords.map(r => [
      `"${r.volunteer_name || ''}"`,
      `"${r.volunteer_email || ''}"`,
      `"${r.volunteer_phone || ''}"`,
      `"${r.class_name || ''}"`,
      `"${r.date}"`,
      `"${r.day}"`,
      `"${(r.sibling_classes || []).join(', ')}"`
    ])

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `ilanot-family-duties-${selectedSchoolYear}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="space-y-6 text-right" dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 font-hebrew flex items-center gap-2">
            <span>👨‍👩‍👧‍👦</span>
            <span>דוח משפחות, אחים וחישוב הקצאות</span>
          </h2>
          <p className="text-sm text-gray-500 font-hebrew mt-1">
            מעקב אחר עקרון "תורנות אחת למשפחה", פטורים מכיתות אחים, ומחשבון פתיחת ימים חכם.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <select
            value={selectedSchoolYear}
            onChange={(e) => setSelectedSchoolYear(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-hebrew bg-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="2026-2027">שנת תשפ"ז (2026–2027)</option>
            <option value="2025-2026">שנת תשפ"ו (2025–2026)</option>
          </select>

          <button
            onClick={fetchFamilyData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
            title="רענן נתונים"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 font-hebrew">סה"כ משפחות רשומות</p>
            <p className="text-2xl sm:text-3xl font-extrabold text-blue-600 font-hebrew mt-1">{totalFamilies}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-2xl">
            📝
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 font-hebrew">משפחות עם מספר ילדים</p>
            <p className="text-2xl sm:text-3xl font-extrabold text-indigo-600 font-hebrew mt-1">{multiChildFamilies.length}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl">
            👨‍👩‍👧‍👦
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 font-hebrew">תורנויות פטורות שנחסכו</p>
            <p className="text-2xl sm:text-3xl font-extrabold text-green-600 font-hebrew mt-1">{totalExemptionsCount}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center text-2xl">
            ✨
          </div>
        </div>
      </div>

      {/* Smart Class Quota Calculator */}
      <div className="bg-gradient-to-br from-indigo-50/70 via-white to-blue-50/70 p-5 sm:p-6 rounded-2xl border border-indigo-200/80 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-indigo-100">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-indigo-950 font-hebrew flex items-center gap-2">
              <span>🧮</span>
              <span>מחשבון פתיחת ימים לכיתה הבאה</span>
            </h3>
            <p className="text-xs sm:text-sm text-indigo-800/80 font-hebrew mt-0.5">
              חישוב אוטומטי של מספר ימי התורנות הנדרשים לכיתה לאחר הפחתת משפחות שכבר תרמו דרך אחים.
            </p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div>
              <label className="block text-[11px] font-semibold text-indigo-900 font-hebrew mb-1">כיתה יעד:</label>
              <select
                value={calculatorTargetClass}
                onChange={(e) => setCalculatorTargetClass(e.target.value)}
                className="px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-hebrew bg-white focus:ring-2 focus:ring-indigo-500 font-bold text-indigo-900"
              >
                {ALL_CLASSES.map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-indigo-900 font-hebrew mb-1">תלמידים בכיתה:</label>
              <input
                type="number"
                min="1"
                max="45"
                value={calculatorBaseStudents}
                onChange={(e) => setCalculatorBaseStudents(Number(e.target.value) || 0)}
                className="w-20 px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-hebrew text-center bg-white focus:ring-2 focus:ring-indigo-500 font-bold text-indigo-900"
              />
            </div>
          </div>
        </div>

        {/* Calculation Result */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="bg-white/80 p-4 rounded-xl border border-indigo-100 flex flex-col justify-center">
            <span className="text-xs text-gray-500 font-hebrew font-medium">בסיס תלמידים בכיתה:</span>
            <span className="text-xl font-bold text-gray-900 font-hebrew">{calculatorBaseStudents} משפחות</span>
          </div>

          <div className="bg-white/80 p-4 rounded-xl border border-indigo-100 flex flex-col justify-center">
            <span className="text-xs text-green-700 font-hebrew font-medium">פטורים (אחים שתורמו):</span>
            <span className="text-xl font-bold text-green-600 font-hebrew">-{targetClassExemptions.length} משפחות</span>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 rounded-xl text-white shadow-md flex items-center justify-between">
            <div>
              <span className="text-xs text-blue-100 font-hebrew font-medium">המלצת פתיחה לכיתה:</span>
              <span className="text-2xl font-black font-hebrew block">{calculatedRequiredDays} ימי תורנות</span>
            </div>
            <button
              onClick={handleCopySql}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-xs font-hebrew font-bold transition-all backdrop-blur-sm border border-white/30 flex items-center gap-1 active:scale-95"
            >
              {copiedSql ? (
                <>
                  <span>✓</span>
                  <span>הועתק!</span>
                </>
              ) : (
                <>
                  <span>📋</span>
                  <span>העתק SQL</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Breakdown of Exempt Families for Target Class */}
        {targetClassExemptions.length > 0 ? (
          <div className="mt-4 pt-3 border-t border-indigo-100/80">
            <p className="text-xs font-bold text-indigo-950 font-hebrew mb-2">
              משפחות מ{calculatorTargetClass} שכבר ביצעו תורנות בכיתה אחרת:
            </p>
            <div className="flex flex-wrap gap-2">
              {targetClassExemptions.map(slot => (
                <div
                  key={slot.id}
                  className="px-2.5 py-1 bg-white border border-indigo-200 rounded-lg text-xs font-hebrew text-indigo-900 shadow-2xs flex items-center gap-1.5"
                >
                  <span className="font-bold">{slot.volunteer_name}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-indigo-600">ביצע ב-{slot.class_name} ({slot.date})</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500 font-hebrew mt-3">
            טרם נרשמו משפחות עם אחים מ{calculatorTargetClass}.
          </p>
        )}
      </div>

      {/* Families & Duties Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Table Controls */}
        <div className="p-4 sm:p-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="חיפוש לפי שם, אימייל, כיתה..."
                className="w-full pl-3 pr-9 py-2 border border-gray-300 rounded-lg text-sm font-hebrew text-right focus:ring-2 focus:ring-blue-500"
              />
              <span className="absolute right-3 top-2.5 text-gray-400">🔍</span>
            </div>

            <div className="flex items-center bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1 text-xs font-hebrew rounded-md transition-colors ${
                  filterType === 'all' ? 'bg-white text-gray-900 font-bold shadow-xs' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                כל המשפחות ({activeYearSlots.length})
              </button>
              <button
                onClick={() => setFilterType('siblings_only')}
                className={`px-3 py-1 text-xs font-hebrew rounded-md transition-colors ${
                  filterType === 'siblings_only' ? 'bg-white text-gray-900 font-bold shadow-xs' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                עם אחים בלבד ({multiChildFamilies.length})
              </button>
            </div>
          </div>

          <button
            onClick={handleExportCsv}
            disabled={filteredRecords.length === 0}
            className="px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-hebrew font-bold transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            <span>📥</span>
            <span>ייצוא ל-Excel / CSV</span>
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-right font-hebrew">
            <thead className="bg-gray-50 text-gray-600 text-xs font-bold border-b border-gray-200">
              <tr>
                <th className="py-3 px-4">שם ההורה</th>
                <th className="py-3 px-4">פרטי קשר</th>
                <th className="py-3 px-4">כיתת תורנות</th>
                <th className="py-3 px-4">תאריך תורנות</th>
                <th className="py-3 px-4">כיתות אחים פטורות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400">
                    טוען נתוני משפחות...
                  </td>
                </tr>
              ) : filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400">
                    לא נמצאו רשומות מתאימות.
                  </td>
                </tr>
              ) : (
                filteredRecords.map(record => {
                  const siblingClasses = record.sibling_classes || []

                  return (
                    <tr key={record.id} className="hover:bg-gray-50/70 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-gray-900">
                        {record.volunteer_name || 'מתנדב'}
                      </td>
                      <td className="py-3.5 px-4 text-xs text-gray-600">
                        <p>{record.volunteer_email}</p>
                        {record.volunteer_phone && (
                          <p className="text-gray-400 mt-0.5" dir="ltr">{record.volunteer_phone}</p>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold border border-blue-200">
                          {record.class_name}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-xs text-gray-700">
                        <p className="font-semibold">{record.date}</p>
                        <p className="text-gray-400">{record.day}</p>
                      </td>
                      <td className="py-3.5 px-4">
                        {siblingClasses.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {siblingClasses.map(cls => (
                              <span
                                key={cls}
                                className="px-2 py-0.5 bg-green-100 text-green-800 rounded-md text-xs font-bold border border-green-200 flex items-center gap-1"
                              >
                                <span>{cls}</span>
                                <span className="text-[10px] text-green-600">(פטור)</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">ללא אחים נוספים</span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
